import type { LapRef, TelemetryLapSummary } from "@/types";

const LAP_REF_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):([1-9]\d{0,4})$/i;

/** Parse the `<sessionId>:<lapNumber>` form used in comparison URLs. */
export function parseLapRef(value: string | null | undefined): LapRef | null {
  if (!value) return null;
  const match = LAP_REF_PATTERN.exec(value.trim());
  if (!match) return null;
  return { sessionId: match[1], lapNumber: Number(match[2]) };
}

export function formatLapRef(ref: LapRef): string {
  return `${ref.sessionId}:${ref.lapNumber}`;
}

export function sameLapRef(a: LapRef | null, b: LapRef | null): boolean {
  if (!a || !b) return a === b;
  return a.sessionId === b.sessionId && a.lapNumber === b.lapNumber;
}

/** Comparison page URL for the given slots; omitted slots are left out. */
export function buildComparePath({
  ref,
  cmp,
  channels,
}: {
  ref?: LapRef | null;
  cmp?: LapRef | null;
  channels?: string[];
}): string {
  const params = new URLSearchParams();
  if (ref) params.set("ref", formatLapRef(ref));
  if (cmp) params.set("cmp", formatLapRef(cmp));
  if (channels && channels.length > 0) params.set("ch", channels.join(","));
  const query = params.toString();
  return `/laps/compare${query ? `?${query}` : ""}`;
}

/** Laps bounded by two start/finish crossings; only these can be compared. */
export function comparableLaps(
  laps: TelemetryLapSummary[],
): TelemetryLapSummary[] {
  return laps.filter(
    (lap) =>
      lap.isComplete === true &&
      lap.startTime !== undefined &&
      lap.endTime !== undefined,
  );
}
